import React, { useCallback, useEffect, useState } from 'react';
import { PLANS_CONFIG, PRO_MONTHLY_USD, PLAN_COMPARISON } from '../../config/plans';
import { effectivePlanTier, accessSource } from '../../utils/entitlements';
import {
  fetchBillingSummary,
  openBillingPortal,
  startProCheckout,
  cancelStripeSubscription,
  reactivateStripeSubscription,
} from '../../services/billingClient';

/**
 * Premium plan & billing overlay (Stripe checkout, portal, cancel/resume).
 * @param {{
 *   isOpen: boolean;
 *   onClose: () => void;
 *   userData: Record<string, unknown> | null;
 *   onRefreshProfile?: () => Promise<void> | void;
 *   highlightCheckout?: boolean;
 * }} props
 */
export default function BillingPlanModal({ isOpen, onClose, userData, onRefreshProfile, highlightCheckout }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const tier = effectivePlanTier(userData);
  const src = accessSource(userData);

  const loadSummary = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const s = await fetchBillingSummary();
      setSummary(s);
    } catch (e) {
      setSummary(null);
      setError(e?.message || 'Billing service is unavailable. Try again shortly.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadSummary();
  }, [isOpen, loadSummary]);

  useEffect(() => {
    if (!isOpen || !highlightCheckout) return;
    setError('');
  }, [isOpen, highlightCheckout]);

  const onUpgrade = async () => {
    setBusy(true);
    setError('');
    try {
      await startProCheckout();
    } catch (e) {
      setError(e?.message || 'Checkout could not start.');
      setBusy(false);
    }
  };

  const onPortal = async () => {
    setBusy(true);
    setError('');
    try {
      await openBillingPortal();
    } catch (e) {
      setError(e?.message || 'Could not open the billing portal.');
      setBusy(false);
    }
  };

  const billingKind = summary?.billingKind;
  const isPaidStripePro = billingKind === 'paid_pro';
  const isApplePro = billingKind === 'apple_pro' || (tier === 'pro' && src === 'apple');
  const isComplimentaryPro = billingKind === 'manual_pro' || (tier === 'pro' && src === 'manual');
  const showPortal = isPaidStripePro;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="billing-modal-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-[2px]"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative z-[121] flex max-h-[min(92vh,880px)] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl border border-slate-200/80 bg-white shadow-2xl shadow-slate-900/20 sm:rounded-3xl">
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white px-5 py-4 sm:px-8 sm:py-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Plans & billing</p>
            <h2 id="billing-modal-title" className="mt-1 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
              Your workspace plan
            </h2>
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-600">
              Upgrade, manage payment methods, and invoices securely through Stripe.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-8 sm:py-6">
          {error && (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
              {error}
            </div>
          )}

          {highlightCheckout && tier !== 'pro' && (
            <div className="mb-5 rounded-2xl border border-cyan-200 bg-gradient-to-r from-cyan-50 to-white px-4 py-3 text-sm text-cyan-950">
              <p className="font-semibold">Finish Pro checkout</p>
              <p className="mt-1 text-cyan-900/90">
                Your account is on <strong>Free</strong> until payment completes. Continue below to subscribe for $
                {PRO_MONTHLY_USD}/month.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600 ring-1 ring-slate-200/80">
                  Current
                </span>
                {isComplimentaryPro && (
                  <span className="inline-flex items-center rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-900">
                    Complimentary
                  </span>
                )}
                {isPaidStripePro && (
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-900">
                    Paid via Stripe
                  </span>
                )}
                {isApplePro && (
                  <span className="inline-flex items-center rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-900">
                    Paid via App Store
                  </span>
                )}
              </div>
              <p className="mt-2 text-2xl font-bold text-slate-900">
                {tier === 'pro' ? PLANS_CONFIG.pro.name : PLANS_CONFIG.free.name}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {tier === 'pro' ? PLANS_CONFIG.pro.subtitle : PLANS_CONFIG.free.subtitle}
              </p>
              {isComplimentaryPro && (
                <p className="mt-3 text-sm leading-relaxed text-slate-700">
                  You have <strong>complimentary Pro access</strong> (no paid Stripe subscription on file). Add a card
                  and start a subscription whenever you are ready. Billing management becomes available after you start
                  paid Pro.
                </p>
              )}
              {isApplePro && (
                <p className="mt-3 text-sm leading-relaxed text-slate-700">
                  Your Pro subscription is billed by Apple through the Devello iPhone app
                  {summary?.appleExpiresAt
                    ? ` and renews or ends ${new Date(summary.appleExpiresAt).toLocaleDateString()}`
                    : ''}
                  . To change or cancel it, open <strong>Settings → your name → Subscriptions</strong> on your iPhone.
                </p>
              )}
            </div>
            <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:min-w-[200px]">
              {tier !== 'pro' && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={onUpgrade}
                  className="rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-primary/25 transition hover:bg-primary-dark disabled:opacity-50"
                >
                  {busy ? 'Redirecting…' : `Subscribe to Pro — $${PRO_MONTHLY_USD}/mo`}
                </button>
              )}
              {tier === 'pro' && isComplimentaryPro && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={onUpgrade}
                  className="rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-primary/25 transition hover:bg-primary-dark disabled:opacity-50"
                >
                  {busy ? 'Redirecting…' : `Start paid subscription — $${PRO_MONTHLY_USD}/mo`}
                </button>
              )}
              {showPortal && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={onPortal}
                  className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  Manage billing & cards
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Subscription</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{loading ? '…' : summary?.subscriptionStatus || '—'}</p>
              {summary?.currentPeriodEnd && isPaidStripePro && (
                <p className="mt-1 text-xs text-slate-500">Renews or ends {new Date(summary.currentPeriodEnd).toLocaleDateString()}</p>
              )}
            </div>
            <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Default card</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {loading
                  ? '…'
                  : summary?.defaultPaymentMethod
                    ? `${summary.defaultPaymentMethod.brand} •••• ${summary.defaultPaymentMethod.last4}`
                    : 'None on file'}
              </p>
            </div>
          </div>

          {isPaidStripePro && summary?.subscriptionStatus === 'active' && (
            <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-5">
              {summary?.cancelAtPeriodEnd ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    setError('');
                    try {
                      await reactivateStripeSubscription();
                      await loadSummary();
                      if (onRefreshProfile) await onRefreshProfile();
                    } catch (e) {
                      setError(e?.message || 'Could not resume subscription.');
                    } finally {
                      setBusy(false);
                    }
                  }}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
                >
                  Resume subscription
                </button>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    if (
                      !window.confirm(
                        'Cancel at the end of the current billing period? You keep Pro until then. You can also cancel in the Stripe portal.'
                      )
                    ) {
                      return;
                    }
                    setBusy(true);
                    setError('');
                    try {
                      await cancelStripeSubscription({ immediate: false });
                      await loadSummary();
                      if (onRefreshProfile) await onRefreshProfile();
                    } catch (e) {
                      setError(e?.message || 'Could not schedule cancellation.');
                    } finally {
                      setBusy(false);
                    }
                  }}
                  className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-950 hover:bg-amber-100"
                >
                  Schedule cancel at period end
                </button>
              )}
            </div>
          )}

          <div className="mt-8">
            <h3 className="text-sm font-semibold text-slate-900">Compare plans</h3>
            <p className="mt-1 text-xs text-slate-500">Pro is ${PRO_MONTHLY_USD}/month, billed securely by Stripe.</p>
            <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
              <table className="w-full min-w-[280px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/90 text-slate-500">
                    <th className="px-4 py-3 font-medium">Feature</th>
                    <th className="px-3 py-3 font-medium">Free</th>
                    <th className="px-3 py-3 font-medium">Pro</th>
                  </tr>
                </thead>
                <tbody>
                  {PLAN_COMPARISON.features.map((row) => (
                    <tr key={row.name} className="border-b border-slate-50 last:border-0">
                      <td className="px-4 py-2.5 text-slate-700">{row.name}</td>
                      <td className="px-3 py-2.5 text-slate-600">{row.free ? '✓' : '—'}</td>
                      <td className="px-3 py-2.5 text-slate-600">{row.pro ? '✓' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {[PLANS_CONFIG.free, PLANS_CONFIG.pro].map((p) => (
                <div
                  key={p.id}
                  className={`rounded-2xl border p-4 ${
                    (p.id === 'pro' && tier === 'pro') || (p.id === 'free' && tier !== 'pro')
                      ? 'border-primary/50 bg-primary/[0.04] ring-1 ring-primary/15'
                      : 'border-slate-100 bg-white'
                  }`}
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{p.name}</p>
                  <p className="mt-1 text-lg font-bold text-slate-900">
                    {p.price}
                    <span className="text-sm font-medium text-slate-600">{p.period}</span>
                  </p>
                  <ul className="mt-3 space-y-1.5 text-xs text-slate-600">
                    {p.features.map((f) => (
                      <li key={f} className="flex gap-2">
                        <span className="text-emerald-600">✓</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-slate-500">
            Devello never stores your full card number. Taxes and invoices appear in Stripe.
          </p>
        </div>
      </div>
    </div>
  );
}
